import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { format, startOfDay, endOfDay, subDays } from 'date-fns'
import { ru } from 'date-fns/locale/ru'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { selectActiveBuildingId } from '@/features/building/buildingSlice'
import { billingApi } from '@/api/billing'
import {
  useLazyGetBulkDebtPreviewQuery,
  useBulkDebtWriteOffMutation,
} from '@/api/adminServiceApi'
import { closeBulkWriteOffModal, selectShowBulkWriteOffModal } from '../billingSlice'

const API_DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss'
const OPEN_MODE_CLOSE = 'close'
const OPEN_MODE_FREEZE = 'freeze'

const PRESETS = [
  { key: 'today', range: () => [startOfDay(new Date()), endOfDay(new Date())] },
  { key: 'yesterday', range: () => [startOfDay(subDays(new Date(), 1)), endOfDay(subDays(new Date(), 1))] },
  { key: 'last_7_days', range: () => [startOfDay(subDays(new Date(), 6)), endOfDay(new Date())] },
  { key: 'last_30_days', range: () => [startOfDay(subDays(new Date(), 29)), endOfDay(new Date())] },
]

const formatMoney = (value) => `${Math.round(Number(value) || 0).toLocaleString('ru-RU')} ₸`

const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime())

const BulkWriteOffModal = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const show = useSelector(selectShowBulkWriteOffModal)
  const activeBuildingId = useSelector(selectActiveBuildingId)

  const [from, setFrom] = useState(() => startOfDay(subDays(new Date(), 6)))
  const [to, setTo] = useState(() => endOfDay(new Date()))
  const [plateNumber, setPlateNumber] = useState('')
  const [openMode, setOpenMode] = useState(OPEN_MODE_CLOSE)
  const [reason, setReason] = useState('')
  const [confirming, setConfirming] = useState(false)

  const [loadPreview, {
    data: preview,
    isFetching: isPreviewLoading,
    error: previewError,
    reset: resetPreview,
  }] = useLazyGetBulkDebtPreviewQuery()
  const [writeOff, {
    data: result,
    isLoading: isWriteOffLoading,
    isSuccess,
    error: writeOffError,
    reset: resetWriteOff,
  }] = useBulkDebtWriteOffMutation()

  const periodValid = isValidDate(from) && isValidDate(to) && from < to
  const params = periodValid ? {
    buildingId: activeBuildingId,
    from: format(from, API_DATE_FORMAT),
    to: format(to, API_DATE_FORMAT),
    plateNumber: plateNumber.trim().toUpperCase(),
    openMode,
  } : null

  // Любое изменение фильтров делает превью неактуальным.
  const invalidate = () => {
    resetPreview()
    resetWriteOff()
    setConfirming(false)
  }
  const withInvalidate = (setter) => (value) => {
    setter(value)
    invalidate()
  }

  const applyPreset = (range) => {
    const [nextFrom, nextTo] = range()
    setFrom(nextFrom)
    setTo(nextTo)
    invalidate()
  }

  const handlePreview = () => {
    if (!params) return
    resetWriteOff()
    setConfirming(false)
    void loadPreview(params)
  }

  const handleWriteOff = async () => {
    if (!params) return
    try {
      await writeOff({ ...params, reason: reason.trim() }).unwrap()
      dispatch(billingApi.util.invalidateTags(['Billing']))
    } catch {
      // ошибка показывается из состояния мутации
    } finally {
      setConfirming(false)
    }
  }

  const handleClose = () => {
    dispatch(closeBulkWriteOffModal())
    setPlateNumber('')
    setReason('')
    setOpenMode(OPEN_MODE_CLOSE)
    invalidate()
  }

  const hasPreview = !!preview && !isPreviewLoading
  const canWriteOff = hasPreview && preview.sessions_count > 0 && reason.trim().length > 0 && !isSuccess
  const requestError = (writeOffError || previewError) as { data?: { message?: string } } | undefined
  const errorMessage = requestError?.data?.message
  const openModeLabel = openMode === OPEN_MODE_FREEZE
    ? t('bulk_write_off_open_frozen')
    : t('bulk_write_off_open_closed')

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{t('bulk_write_off_title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('bulk_write_off_description')}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {PRESETS.map(({ key, range }) => (
              <Chip
                key={key}
                label={t(`bulk_write_off_preset_${key}`)}
                size="small"
                variant="outlined"
                onClick={() => applyPreset(range)}
              />
            ))}
          </Box>

          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <DateTimePicker
                label={t('bulk_write_off_entrance_from')}
                value={from}
                onChange={withInvalidate(setFrom)}
                {...(isValidDate(to) ? { maxDateTime: to } : {})}
                ampm={false}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
              <DateTimePicker
                label={t('bulk_write_off_entrance_to')}
                value={to}
                onChange={withInvalidate(setTo)}
                {...(isValidDate(from) ? { minDateTime: from } : {})}
                ampm={false}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Stack>
          </LocalizationProvider>

          <TextField
            label={t('bulk_write_off_plate_optional')}
            value={plateNumber}
            onChange={(e) => withInvalidate(setPlateNumber)(e.target.value)}
            size="small"
            fullWidth
          />

          <FormControl>
            <FormLabel>{t('bulk_write_off_open_sessions')}</FormLabel>
            <RadioGroup
              value={openMode}
              onChange={(e) => withInvalidate(setOpenMode)(e.target.value)}
            >
              <FormControlLabel
                value={OPEN_MODE_CLOSE}
                control={<Radio size="small" />}
                label={t('bulk_write_off_mode_close')}
              />
              <FormControlLabel
                value={OPEN_MODE_FREEZE}
                control={<Radio size="small" />}
                label={t('bulk_write_off_mode_freeze')}
              />
            </RadioGroup>
          </FormControl>

          <TextField
            label={t('bulk_write_off_reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            required
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />

          {!periodValid && (
            <Alert severity="warning">{t('bulk_write_off_invalid_period')}</Alert>
          )}

          {requestError ? (
            <Alert severity="error">{errorMessage || t('unknown_error')}</Alert>
          ) : null}

          {isSuccess && result ? (
            <Alert severity="success" sx={{ whiteSpace: 'pre-line' }}>
              {t('bulk_write_off_result', {
                affected: result.affected,
                amount: formatMoney(result.total_written_off),
              })}
              {result.closed_open > 0 && `\n${t('bulk_write_off_result_closed', { count: result.closed_open })}`}
              {result.frozen_open > 0 && `\n${t('bulk_write_off_result_frozen', { count: result.frozen_open })}`}
              {result.remaining > 0 && `\n${t('bulk_write_off_result_remaining', { count: result.remaining })}`}
            </Alert>
          ) : hasPreview && (
            preview.sessions_count === 0 ? (
              <Alert severity="info">{t('bulk_write_off_nothing')}</Alert>
            ) : (
              <Alert severity={confirming ? 'warning' : 'info'} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('bulk_write_off_preview', {
                    count: preview.sessions_count,
                    amount: formatMoney(preview.total_debt),
                  })}
                </Typography>
                {preview.open_count > 0 && (
                  <Typography variant="body2">
                    {t('bulk_write_off_preview_open', { count: preview.open_count, action: openModeLabel })}
                  </Typography>
                )}
                {preview.sessions_count > preview.max_per_request && (
                  <Typography variant="body2">
                    {t('bulk_write_off_preview_limit', { count: preview.max_per_request })}
                  </Typography>
                )}
                {preview.sample?.length > 0 && (
                  <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('plate_number')}</TableCell>
                        <TableCell>{t('date_of_arrival')}</TableCell>
                        <TableCell align="right">{t('billing_debt')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.sample.map((row) => (
                        <TableRow key={row.billing_id}>
                          <TableCell>{row.plate_number}</TableCell>
                          <TableCell>
                            {row.entrance_time.slice(0, 16)}
                            {row.is_open && ` · ${t('bulk_write_off_on_parking')}`}
                          </TableCell>
                          <TableCell align="right">{formatMoney(row.debt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                {confirming && (
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                    {t('bulk_write_off_confirm_question')}
                  </Typography>
                )}
              </Alert>
            )
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="error">
          {t('close')}
        </Button>
        {!isSuccess && (
          confirming ? (
            <>
              <Button onClick={() => setConfirming(false)} disabled={isWriteOffLoading}>
                {t('cancel')}
              </Button>
              <Button
                variant="contained"
                color="warning"
                loading={isWriteOffLoading}
                onClick={handleWriteOff}
              >
                {t('bulk_write_off_confirm')}
              </Button>
            </>
          ) : (
            <>
              <Button
                loading={isPreviewLoading}
                disabled={!periodValid}
                onClick={handlePreview}
              >
                {t('bulk_write_off_calculate')}
              </Button>
              <Button
                variant="contained"
                disabled={!canWriteOff}
                onClick={() => setConfirming(true)}
              >
                {t('bulk_write_off_submit')}
              </Button>
            </>
          )
        )}
      </DialogActions>
    </Dialog>
  )
}

export default BulkWriteOffModal
